import { useQuery } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { v2GetStartupWalletOptions } from '@workspace/api-backend/react-query'
import {
  FounderLayout as BaseFounderLayout,
  type FounderLayoutProps,
} from '@workspace/seedforge-ui/components/founder-layout'
import type { UserMenuLink } from '@workspace/seedforge-ui/components/user-menu'
import {
  FOUNDER_NAV_EMAIL_UPDATES_LABEL,
  FOUNDER_NAV_FEEDBACK_GROUP_LABEL,
  FOUNDER_NAV_INVESTOR_FEEDBACK_LABEL,
  FOUNDER_NAV_REPLIES_LABEL,
  FOUNDER_NAV_SEEDFORGE_FEEDBACK_LABEL,
} from '@workspace/seedforge-ui/lib/founder-nav'
import { LifeBuoy, Shield } from 'lucide-react'
import { useMemo } from 'react'
import { useStartupContext } from '../contexts/startup-guard'
import { useStartupFeatureFlagsQuery } from '../features/feature-flags/hooks/use-startup-feature-flags'
import { useFeedbackNavBadge } from '../features/feedback/hooks/use-feedback-seen'
import { useOutreachNavBadges } from '../features/outreach/hooks/use-outreach-nav-badges'
import { useOutreachStatusQuery } from '../features/outreach/hooks/use-outreach-status'
import { useBasePath } from '../hooks/use-base-path'
import { useSignOut } from '../hooks/use-sign-out'
import { useWorkspaceReady } from '../hooks/use-workspace-ready'
import { useFeedback } from '../routes/app'
import { HomeSidebarCta } from './home-sidebar-cta'

function getInitials(name: string): string {
  return name
    .split(' ')
    .map((w) => w[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)
}

export function FounderLayout(
  props: Omit<
    FounderLayoutProps,
    | 'onSignOut'
    | 'onSendFeedback'
    | 'userName'
    | 'userEmail'
    | 'activeStartup'
    | 'startups'
    | 'onSwitchStartup'
    | 'basePath'
    | 'logoSrc'
    | 'sidebarFooterSlot'
  >
) {
  const { user, startup, startups, switchStartup, billing, hasEvaluation } = useStartupContext()
  const signOut = useSignOut()
  const openFeedback = useFeedback()
  const basePath = useBasePath()
  const navigate = useNavigate()

  const extraLinks = useMemo<UserMenuLink[]>(() => {
    const links: UserMenuLink[] = []
    if (user.role === 'ADMIN') {
      links.push({ icon: Shield, label: 'Admin', to: '/admin' })
    }
    const webUrl = import.meta.env.VITE_WEB_URL ?? 'https://seedforge.com'
    links.push({ icon: LifeBuoy, label: 'Help Center', to: `${webUrl}/help`, external: true })
    return links
  }, [user.role, basePath])

  // Shared with WorkspaceReadyGuard so route-level + nav-level gating stay
  // in sync. `ready` is hydrated from a localStorage cache for an instant
  // first paint; the live matches query updates it once it resolves.
  const { ready } = useWorkspaceReady()
  // Gating is per-leaf. Disabling a group would also lock the leaves that do not
  // depend on the evaluation, so only the two pages built on it are locked here.
  //
  // SeedForge's Feedback and Investor's Feedback have nothing to render until the
  // evaluation read completes. Share Analytics and Outreach Outcomes do not need
  // it, so they stay open.
  //
  // Profile and Proof are never gated. They hold the founder's own data, and the
  // profile page reads evaluation optionally and renders fine without one.
  //
  // Outreach needs matches: without them there is no one to contact, so Outcomes is disabled. The Outreach
  // card and Matched investors in the Share your Profile row are disabled the same way.
  const disabledNavLabels = [
    ...(!ready ? [FOUNDER_NAV_REPLIES_LABEL] : []),
    ...(!hasEvaluation
      ? [FOUNDER_NAV_SEEDFORGE_FEEDBACK_LABEL, FOUNDER_NAV_INVESTOR_FEEDBACK_LABEL]
      : []),
  ]

  // The Email updates item exists only while the EMAIL_UPDATES flag is on.
  const featureFlags = useStartupFeatureFlagsQuery(startup.id)
  const hiddenNavLabels = featureFlags.data?.emailUpdates ? [] : [FOUNDER_NAV_EMAIL_UPDATES_LABEL]

  const outreachBadges = useOutreachNavBadges(startup.id, { enabled: ready })
  // The founder is told the feedback will arrive by email and then has no way
  // to tell it landed without opening the page. The dot is that signal, and it
  // clears itself the first time they read it. Gated on the group being
  // enabled, so it cannot point at a section they cannot click.
  const feedbackBadge = useFeedbackNavBadge(startup.id, { enabled: hasEvaluation })
  const navBadges = feedbackBadge
    ? {
        ...outreachBadges,
        [FOUNDER_NAV_SEEDFORGE_FEEDBACK_LABEL]: feedbackBadge,
        [FOUNDER_NAV_FEEDBACK_GROUP_LABEL]: feedbackBadge,
      }
    : outreachBadges

  // Wallet balance for the topbar billing popover — query key shared with the
  // outreach and billing pages, so navigating there reuses this cache.
  const walletQuery = useQuery(v2GetStartupWalletOptions({ path: { id: startup.id } }))
  const walletBalanceCents =
    walletQuery.data?.status === 'ok' ? walletQuery.data.data.balanceCents : 0
  // Same trial the Billing tab reports, so the topbar cannot say something
  // different from the page it links to.
  const outreachStatus = useOutreachStatusQuery(startup.id)
  const trialEndsAt = outreachStatus.data?.trialEndsAt ?? null
  const onFreeTrial = trialEndsAt !== null && new Date(trialEndsAt) > new Date()

  const startupItems = startups.map((s) => ({
    id: s.id,
    name: s.name,
    initials: getInitials(s.name),
    role: s.role,
  }))

  const activeItem = {
    id: startup.id,
    name: startup.name,
    initials: getInitials(startup.name),
  }

  return (
    <BaseFounderLayout
      {...props}
      basePath={basePath}
      logoSrc="/seedforge-icon.svg"
      userName={user.name ?? undefined}
      userEmail={user.email}
      promoSession={billing?.promoSession ?? false}
      billing={{
        walletBalanceCents,
        onFreeTrial,
      }}
      navBadges={navBadges}
      onSignOut={signOut}
      onSendFeedback={openFeedback}
      activeStartup={activeItem}
      startups={startupItems}
      onSwitchStartup={switchStartup}
      onStartupNameClick={() => navigate({ to: basePath })}
      userMenuExtraLinks={extraLinks}
      sidebarFooterSlot={<HomeSidebarCta startupId={startup.id} />}
      disabledNavLabels={disabledNavLabels}
      hiddenNavLabels={hiddenNavLabels}
      disabledNavTooltip="Available once your deck is processed and investors are matched"
    />
  )
}
